import { sendAnalytics } from '@feelrobotics/ftap-connector';
import { sendToAnalytics } from 'lib/googleAnalytics';
import { shouldSendAnalytics } from 'utils/utils';
import { BaseDeviceWrapper } from './BaseDeviceWrapper';
import image from 'images/devices/sam-neo-2-promotion.png';

const SERVICE_UUID = 0xffe0;
const CHAR_UUID = 0xffe1;

const IDLE_TIMEOUT_BEFORE_AUTO_CONTROL_MODE = 2000; // timeout in milliseconds before switching toy to ambient mode
const STOP_MOTORS = 0;
// mode mumber three has more intensity.
const VIBRATION_MODE = 3;

const names = ['Sam Neo 2', 'Sam Neo 2 Pro'];

/**
 * Create a device wrapper over the Web Bluetooth device object
 * @param {obj} webBleDevice - Web Bluetooth device object
 */
export default class SvakomSamNeo extends BaseDeviceWrapper {
  maxIntensity: number;
  ambientIdleTimeout: null | NodeJS.Timeout;
  suctionMode: number;
  statMotorIntensity: number;
  statMotorMode: number;
  statSuctionMode: number;
  statAmbientMode: number;
  constructor(device) {
    super(device, SERVICE_UUID, CHAR_UUID, image);
    this.sensorChar = null;
    this.maxIntensity = 100;
    this.suctionMode = 0;
    this.ambientIdleTimeout = null;

    this.statMotorIntensity = 0;
    this.statMotorMode = 0;
    this.statSuctionMode = 0;
    this.statAmbientMode = 0;
  }

  static get deviceNames() {
    return names;
  }

  /**
   * Needed to request access to these services before connecting to the device
   */
  static get services() {
    return [SERVICE_UUID];
  }

  get companyName() {
    return 'Svakom';
  }

  async connect() {
    const startTime = performance.now();
    await super.connect();

    // Some firmware have suction and vibraiton on when device is turned on
    // Need to turn them off
    this.changeSuction(STOP_MOTORS);
    this.changeVibration(STOP_MOTORS);

    // Report to google analytics
    const milliseconds = Math.round(performance.now() - startTime);
    sendToAnalytics('device_connect', 'device', this.device.name, milliseconds);
    shouldSendAnalytics() && sendToAnalytics('connect', this.device.name, this.device.id);
    sendAnalytics('connect', this.device.name, this.device.id);
  }

  async disconnect() {
    await super.disconnect();
  }

  /**
   * Send vibration command to device
   * @param {number} motorIntensity - Intensity vibration from 0-10
   * @param {number} motorMode - Vibration mode from 0-5:
   * 0: stop vibration
   * 1: _ _ _ _ _
   * 2: ... ___
   * 3: ....
   * 4: constantly vibration
   * 5: .... short with decreasing intensity
   */
  async writeVibration(motorIntensity, motorMode) {
    const data = [0x55, 0x03, 0x00, 0x00, motorMode, motorIntensity, 0x00];
    const array = new Uint8Array(data);
    try {
      await this.motorChar.writeValue(array);
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * Send saction command to device
   * @param {number} suctionMode - pattern number 0..5
   */
  async writeSuctionMode(suctionMode) {
    const data = [0x55, 0x09, 0x00, 0x0, suctionMode, suctionMode, 0x0];
    const array = new Uint8Array(data);
    try {
      await this.motorChar.writeValue(array);
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * A method for completely stopping device activity.
   */
  async writePaused() {
    clearTimeout(this.ambientIdleTimeout);
    this.ambientIdleTimeout = null;

    this.changeVibration(STOP_MOTORS, STOP_MOTORS);
    this.changeSuction(STOP_MOTORS);
  }

  /**
   * Method for preventing duplicate vibration commands for a device
   *
   * @param {number} intensity - Intensity vibration from 0-10
   * @param {number} mode - Vibration mode from 0-5
   * @returns
   */
  async changeVibration(intensity: number, mode?: number) {
    if (this.statMotorIntensity === intensity && this.statMotorMode === mode) {
      return;
    }

    let motorMode = STOP_MOTORS;
    let motorIntensity = STOP_MOTORS;
    if (intensity !== STOP_MOTORS) {
      motorMode = mode ?? VIBRATION_MODE;
      motorIntensity = intensity;
    }

    this.statMotorMode = motorMode;
    this.statMotorIntensity = motorIntensity;
    this.writeVibration(motorIntensity, motorMode);
  }

  /**
   * Method for preventing duplicate suction commands for a device
   * @param {number} suctionMode - pattern number 0..5
   */
  async changeSuction(suctionMode) {
    if (this.statSuctionMode !== suctionMode) {
      this.statSuctionMode = suctionMode;
      this.writeSuctionMode(suctionMode);
    }
  }

  /**
   * Method for entering commands from the ML server
   * @param {number} percent - 0..100
   * @param {number} speed
   * @param {boolean} isBlowJob - true/false
   */
  async write(percent, speed, isBlowJob) {
    let _suctionMode = isBlowJob ? this.suctionMode : STOP_MOTORS;
    await this.changeSuction(_suctionMode);

    let intensity = 0;
    if (percent > 0) {
      clearTimeout(this.ambientIdleTimeout);
      this.ambientIdleTimeout = null;

      // Adjust the percent according to the max intensity
      const adjustedPercent = percent > 70 ? this.maxIntensity : 0;
      intensity = Math.round(adjustedPercent);
    } else {
      // no penetration -> set 0 to the device and start timeout to use Auto Control
      if (!this.ambientIdleTimeout) {
        this.ambientIdleTimeout = setTimeout(async () => {
          if (this.statAmbientMode) {
            this.changeSuction(STOP_MOTORS);
            this.changeVibration(this.maxIntensity, this.statAmbientMode);
          }
        }, IDLE_TIMEOUT_BEFORE_AUTO_CONTROL_MODE);
      }
    }
    this.changeVibration(intensity, VIBRATION_MODE);
  }

  /**
   * Set the suction mode
   * @param {number} mode - Suction mode from 1-5
   */
  async setSuctionMode(mode) {
    this.suctionMode = parseInt(mode);
  }

  async setAmbientMovement(percent) {
    let ambientMode = Math.round(parseInt(percent) / 25);
    if (this.statAmbientMode === ambientMode) {
      return;
    }

    this.statAmbientMode = ambientMode;
    this.changeVibration(this.maxIntensity, this.statAmbientMode);
  }

  async setMaxIntensity(percent) {
    this.maxIntensity = Math.round(Math.max(0, Math.min(percent, 99) / 10));
    if (this.ambientIdleTimeout) {
      this.changeVibration(this.maxIntensity, this.statAmbientMode);
    }
  }
}
